/* globals BaseUrl */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import { helperGetBaseUrlSlug } from 'helpers/RouteHelper';
import { setHelmetTags } from 'helpers/HelmetHelper';
import { LABEL_CLIPS_AND_EXTRAS } from 'helpers/ContentHelper';
import { fetchSiteContent } from './SiteMapActions';
import {
  createLink,
  createListLink,
  extractLinks,
  filterSitemap,
  LABEL_ABOUT,
  LABEL_CHANNEL,
  LABEL_CREDITS,
  LABEL_EPISODES,
  LABEL_LEGAL,
  LABEL_MAIN,
  LABEL_MOVIES,
  LABEL_PERSONALITIES,
  LABEL_SHOWS,
  LABEL_SITEMAP,
  TYPE_CHANNEL,
  TYPE_CLIPS_LANDING,
  TYPE_MOVIE_DETAIL,
  TYPE_PERSONALITY_DETAIL,
  TYPE_SHOW_DETAIL,
} from './SiteMapHelper';
import style from './SiteMap.css';

class SiteMap extends Component {
  componentDidMount() {
    const { content, fetchSiteContent } = this.props;
    if (content) fetchSiteContent(true);
  }

  renderDefaultItem = (item, idx) => {
    if (!item) return null;
    return (
      <div className={style.listGroup} key={`${item.title}${idx}`}>
        <div className={style.listGroupItem}>{createLink(item.url, item.title)}</div>
      </div>
    );
  };

  renderLegalItems() {
    const { legal } = this.props;
    const legalLinks = extractLinks(legal)?.filter(item => item.text !== 'year');
    const legalItems = legalLinks && legalLinks.map(item => createListLink(item.link, item.text));

    return (
      <div className={style.mainListContainer}>
        <h2 className={style.listHeader}>{LABEL_LEGAL}</h2>
        <ul className={style.list}>{legalItems}</ul>
      </div>
    );
  }

  renderMainItems() {
    const { links, networks } = this.props;
    const nav = _.get(window.ExternalConfig, 'navigation', []);
    const mainItems =
      nav &&
      nav.map(item => {
        const url = `${helperGetBaseUrlSlug()}${item.link}`;
        const text = item.label;
        return createListLink(url, text);
      });

    const linkHeader = _.get(links, 'title');
    const linkDetails = _.get(links, 'items', []);
    const linkItems = linkDetails.map(item => createListLink(item.link, item.text));

    const networkHeader = _.get(networks, 'title');
    const networkData = _.get(networks, 'items', []);
    const networkItems = networkData.map(item => createListLink(item.link, item.text));

    return (
      <div className={style.listContainer}>
        <div className={style.mainListContainer}>
          <h2 className={style.listHeader}>{LABEL_MAIN}</h2>
          <ul className={style.list}>{mainItems}</ul>
        </div>

        <div className={style.mainListContainer}>
          {linkHeader && <h2 className={style.listHeader}>{linkHeader} </h2>}
          <ul className={style.list}>{linkItems}</ul>
        </div>

        <div className={style.mainListContainer}>
          {networkHeader && <h2 className={style.listHeader}>{networkHeader}</h2>}
          <ul className={style.list}>{networkItems}</ul>
        </div>
      </div>
    );
  }

  renderMovies() {
    const { content } = this.props;
    const movies = filterSitemap(content, TYPE_MOVIE_DETAIL);
    if (!movies.length) return null;

    return (
      <div className={style.listContainer}>
        <h2 className={style.listHeader}>{LABEL_MOVIES}</h2>
        <div className={style.list}>{movies.map(this.renderDefaultItem)}</div>
      </div>
    );
  }

  /**
   * Todo: there should be single function for all renderers
   */
  renderChannel() {
    const { content } = this.props;
    const channels = filterSitemap(content, TYPE_CHANNEL);
    if (!channels.length) return null;

    return (
      <div className={style.listContainer}>
        <h2 className={style.listHeader}>{LABEL_CHANNEL}</h2>
        <div className={style.list}>{channels.map(this.renderDefaultItem)}</div>
      </div>
    );
  }

  renderPersonalities() {
    const { content } = this.props;
    const personalities = filterSitemap(content, TYPE_PERSONALITY_DETAIL);
    if (!personalities.length) return null;

    return (
      <div className={style.listContainer}>
        <h2 className={style.listHeader}>{LABEL_PERSONALITIES}</h2>
        <div className={style.list}>{personalities.map(this.renderDefaultItem)}</div>
      </div>
    );
  }

  renderSeries() {
    const { content } = this.props;
    const series = filterSitemap(content, TYPE_SHOW_DETAIL);
    if (!series.length) return null;

    return (
      <div className={style.listContainer}>
        <h2 className={style.listHeader}>{LABEL_SHOWS}</h2>
        <div className={style.list}>{series.map(this.renderSeriesItem)}</div>
      </div>
    );
  }

  renderSeriesItem = (item, idx) => {
    if (!item) return null;
    const { subUrls, isRetired, title, url } = item;
    const defaultLinkName = isRetired ? LABEL_ABOUT : LABEL_EPISODES;
    const subUrlItems =
      !_.isEmpty(subUrls) &&
      item.subUrls.map(sub => {
        const { url, type } = sub;
        const isClips = type === TYPE_CLIPS_LANDING && url.endsWith('/clips/');
        const isCredits = type === TYPE_SHOW_DETAIL && url.endsWith('/credits/');
        if (isClips) return createListLink(url, LABEL_CLIPS_AND_EXTRAS);
        if (isCredits) return createListLink(url, LABEL_CREDITS);
        return null;
      });

    const ariaLabel = isRetired ? `${defaultLinkName} ${title}` : `${title} ${defaultLinkName}`;

    return (
      <ul className={style.seriesListGroup} key={`${url}${idx}`}>
        <li className={style.seriesListGroupTitle}>{createLink(url, title, true, ariaLabel)}</li>
        {!isRetired || (isRetired && _.isEmpty(subUrls))
          ? createListLink(url, defaultLinkName, ariaLabel)
          : null}
        {subUrlItems}
      </ul>
    );
  };

  render() {
    const canonicalLink = { canonicalUrl: `${BaseUrl}/sitemap/` };

    return (
      <div className={style.contentContainer}>
        {setHelmetTags(canonicalLink)}
        <h1 className={style.sitemapTitle}>{LABEL_SITEMAP}</h1>
        {this.renderMainItems()}
        {this.renderSeries()}
        {this.renderMovies()}
        {this.renderChannel()}
        {this.renderPersonalities()}
        {this.renderLegalItems()}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    content: state.siteMap.content,
    links: state.footer.links,
    networks: state.footer.networks,
    legal: state.footer.legal,
  };
}

SiteMap.fetchData = ({ store }) => store.dispatch(fetchSiteContent());

SiteMap.defaultProps = {
  content: [],
  legal: null,
  links: [],
  networks: [],
};

SiteMap.propTypes = {
  fetchSiteContent: PropTypes.func.isRequired,
  content: PropTypes.arrayOf(PropTypes.shape({})),
  links: PropTypes.shape({
    title: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  networks: PropTypes.arrayOf(PropTypes.shape({})),
  legal: PropTypes.string,
};

export default connect(mapStateToProps, {
  fetchSiteContent,
})(SiteMap);
